import { defineAthenaTenantConfig } from "@/configuration/ehr/athena/defaults";
import { referralDocumentTypeId } from "@/configuration/ehr/athena/policies";
import { AthenaReferringProviderMatchStrategy, PatientPrimaryDepartment } from "@/configuration/ehr/athena/types";
import {
  TprBiltmoreAhiProviderNpi,
  TprBiltmoreArrowheadProviderNpi,
  TprBiltmoreCamelbackProviderNpi,
  TprBiltmoreCasaGrandeProviderNpi,
  TprBiltmoreGoodyearProviderNpi,
  isTprBiltmoreTelehealthProviderNpi,
  TprBiltmorePeoriaProviderNpi,
  TprBiltmoreScottsdaleProviderNpi,
  TprBiltmoreWestProviderNpi,
  type TprBiltmoreNetworkProviderNpi,
} from "@/configuration/groups/tpr/configuration";
import { getGroupConfigSubdomain } from "@/configuration/paths";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import { logAndThrow } from "@/src/lib/logging/logger";
import { AppointmentType } from "@/src/models/appointment";
import type { Coverage } from "@/src/models/coverage";
import type { Group } from "@/src/models/group";

export const TPR_BILTMORE_DEPARTMENT_ID = "648";
export const TPR_BILTMORE_AHI_DEPARTMENT_ID = "625";
export const TPR_BILTMORE_ARROWHEAD_DEPARTMENT_ID = "381";
export const TPR_BILTMORE_GOODYEAR_DEPARTMENT_ID = "622";
export const TPR_BILTMORE_WEST_DEPARTMENT_ID = "683";
export const TPR_BILTMORE_VERRADO_DEPARTMENT_ID = "747";
export const TPR_BILTMORE_SURPRISE_DEPARTMENT_ID = "708";
export const TPR_BILTMORE_PEORIA_DEPARTMENT_ID = "624";
export const TPR_BILTMORE_SCOTTSDALE_DEPARTMENT_ID = "594";
export const TPR_BILTMORE_CENTRAL_DEPARTMENT_ID = "576";
export const TPR_BILTMORE_CASA_GRANDE_DEPARTMENT_ID = "523";
export const TPR_ATHENA_SELF_PAY_INSURANCE_PACKAGE_ID = "0";
export const TPR_VA_CCN_CASE_POLICY_PACKAGE_ID = 290666;
export const TPR_RIVER_PEOPLE_CASE_POLICY_PACKAGE_ID = 3065016;
export const TPR_SALT_RIVER_CHS_CASE_POLICY_PACKAGE_ID = 261545;

// Display names for every Biltmore Cardiology department in Athena practice 600, used by the
// analytics department filter/export labels (getDepartmentIds/getDepartmentName). Deliberately
// separate from `core.departments` below, which stays scoped to Camelback.
export const TPR_DEPARTMENT_NAMES: Readonly<Record<string, string>> = {
  [TPR_BILTMORE_DEPARTMENT_ID]: "Camelback",
  [TPR_BILTMORE_AHI_DEPARTMENT_ID]: "AHI",
  [TPR_BILTMORE_ARROWHEAD_DEPARTMENT_ID]: "Arrowhead",
  [TPR_BILTMORE_GOODYEAR_DEPARTMENT_ID]: "Goodyear",
  [TPR_BILTMORE_WEST_DEPARTMENT_ID]: "West",
  [TPR_BILTMORE_VERRADO_DEPARTMENT_ID]: "Verrado",
  [TPR_BILTMORE_SURPRISE_DEPARTMENT_ID]: "Surprise",
  [TPR_BILTMORE_PEORIA_DEPARTMENT_ID]: "Peoria",
  [TPR_BILTMORE_SCOTTSDALE_DEPARTMENT_ID]: "Scottsdale",
  [TPR_BILTMORE_CENTRAL_DEPARTMENT_ID]: "Central",
  [TPR_BILTMORE_CASA_GRANDE_DEPARTMENT_ID]: "Casa Grande",
};

export function getTprDepartmentIds(): string[] {
  return Object.keys(TPR_DEPARTMENT_NAMES);
}

export function getTprDepartmentName(departmentId: string): string | null {
  return TPR_DEPARTMENT_NAMES[departmentId] ?? null;
}

export function getTprDepartmentIdFromLocationName(locationName: string | null | undefined): string | null {
  if (!locationName) return null;

  const normalizedLocationName = ` ${locationName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()} `;
  const matches = Object.entries(TPR_DEPARTMENT_NAMES).filter(([, departmentName]) => {
    const normalizedDepartmentName = departmentName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();

    return normalizedLocationName.includes(` ${normalizedDepartmentName} `);
  });

  return matches.length === 1 ? (matches[0]?.[0] ?? null) : null;
}

type TprCasePolicyRule = {
  insurancePackageId: number;
  customerPayerIds: readonly string[];
  payerPattern: RegExp;
};

const TPR_CASE_POLICY_RULES: readonly TprCasePolicyRule[] = [
  {
    insurancePackageId: TPR_VA_CCN_CASE_POLICY_PACKAGE_ID,
    customerPayerIds: ["VACCN", "TWVACCN"],
    payerPattern:
      /optum\s+va\s+community\s+care\s+network|veterans\s+affairs\s+community\s+care\s+network|triwest.*va\s+ccn/i,
  },
  {
    insurancePackageId: TPR_SALT_RIVER_CHS_CASE_POLICY_PACKAGE_ID,
    customerPayerIds: [],
    payerPattern: /river\s+people\s+health\s+care.*purchased\s+referred\s+care|salt\s+river\s+chs/i,
  },
  {
    insurancePackageId: TPR_RIVER_PEOPLE_CASE_POLICY_PACKAGE_ID,
    customerPayerIds: [],
    payerPattern: /river\s+people\s+health\s+center|\brphc\b/i,
  },
];

function resolveTprCasePolicyPackageId(coverage: Coverage): number | null {
  const customerPayer = coverage.customerPayer?.trim().toUpperCase();
  const coverageText = [coverage.payer, coverage.plan]
    .filter((value): value is string => Boolean(value?.trim()))
    .join(" ");
  const rule = TPR_CASE_POLICY_RULES.find(
    ({ customerPayerIds, payerPattern }) =>
      (customerPayer !== undefined && customerPayerIds.includes(customerPayer)) || payerPattern.test(coverageText)
  );

  return rule?.insurancePackageId ?? null;
}

// Request for Additional Information
export const TPR_MISSING_INFO_DOCUMENT_TYPE_ID = 390522;
// Request for Authorization - Referral
export const TPR_REFERRAL_AUTH_DOCUMENT_TYPE_ID = 395521;
const TPR_REFERRAL_SOURCE_ID = "85";

const TPR_BILTMORE_APPOINTMENT_TYPE_IDS: Partial<Record<AppointmentType, number>> = {
  [AppointmentType.INITIAL_CONSULTATION]: 3624,
  [AppointmentType.NEW_PATIENT_CHECKIN]: 3624,
  [AppointmentType.FOLLOW_UP]: 3116,
  [AppointmentType.EXISTING_PATIENT_CHECKIN]: 766,
  [AppointmentType.TELEHEALTH]: 3673,
};

enum TprBiltmoreOpenAppointmentTypeId {
  ANY_10 = 2959,
  ANY_15 = 2962,
  ANY_20 = 2966,
  ANY_30 = 2968,
  ESTABLISHED_PATIENT_10 = 210,
  ESTABLISHED_PATIENT_15 = 766,
  ESTABLISHED_PATIENT_20 = 211,
  ESTABLISHED_PATIENT_30 = 212,
  HOSPITAL_FOLLOW_UP_10 = 3005,
  HOSPITAL_FOLLOW_UP_15 = 3116,
  HOSPITAL_FOLLOW_UP_20 = 3006,
  HOSPITAL_FOLLOW_UP_30 = 3536,
  NEW_PATIENT_10 = 166,
  NEW_PATIENT_15 = 3624,
  NEW_PATIENT_30 = 3625,
  NEW_PATIENT_40 = 3626,
  TELEMED_10 = 3672,
  TELEMED_15 = 3673,
}

const TPR_BILTMORE_ANY_OPEN_APPOINTMENT_TYPE_IDS: ReadonlySet<number> = new Set([
  TprBiltmoreOpenAppointmentTypeId.ANY_10,
  TprBiltmoreOpenAppointmentTypeId.ANY_15,
  TprBiltmoreOpenAppointmentTypeId.ANY_20,
  TprBiltmoreOpenAppointmentTypeId.ANY_30,
]);

const TPR_BILTMORE_BOOKED_APPOINTMENT_TYPES: Record<number, AppointmentType> = {
  [TprBiltmoreOpenAppointmentTypeId.ANY_10]: AppointmentType.UNKNOWN,
  [TprBiltmoreOpenAppointmentTypeId.ANY_15]: AppointmentType.UNKNOWN,
  [TprBiltmoreOpenAppointmentTypeId.ANY_20]: AppointmentType.UNKNOWN,
  [TprBiltmoreOpenAppointmentTypeId.ANY_30]: AppointmentType.UNKNOWN,
  [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_10]: AppointmentType.EXISTING_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15]: AppointmentType.EXISTING_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_20]: AppointmentType.EXISTING_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_30]: AppointmentType.EXISTING_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_10]: AppointmentType.FOLLOW_UP,
  [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_15]: AppointmentType.FOLLOW_UP,
  [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_20]: AppointmentType.FOLLOW_UP,
  [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_30]: AppointmentType.FOLLOW_UP,
  [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_10]: AppointmentType.NEW_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_15]: AppointmentType.NEW_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_30]: AppointmentType.NEW_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_40]: AppointmentType.NEW_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.TELEMED_10]: AppointmentType.TELEHEALTH,
  [TprBiltmoreOpenAppointmentTypeId.TELEMED_15]: AppointmentType.TELEHEALTH,
};

export function getTprBiltmoreAppointmentTypeFromBookedEhrId(
  _providerEhrId: string,
  appointmentTypeId: number
): AppointmentType {
  return TPR_BILTMORE_BOOKED_APPOINTMENT_TYPES[appointmentTypeId] ?? AppointmentType.UNKNOWN;
}

// Athena's generic "Follow Up" type is marked "Do Not Use" and is intentionally not mapped.

type TprBiltmoreProviderOpenAppointmentTypes = {
  any: TprBiltmoreOpenAppointmentTypeId | null;
  newPatient: readonly TprBiltmoreOpenAppointmentTypeId[];
  establishedPatient: readonly TprBiltmoreOpenAppointmentTypeId[];
  hospitalFollowUp: readonly TprBiltmoreOpenAppointmentTypeId[];
  telehealth: readonly TprBiltmoreOpenAppointmentTypeId[];
};

const TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES: TprBiltmoreProviderOpenAppointmentTypes = {
  any: null,
  newPatient: [],
  establishedPatient: [],
  hospitalFollowUp: [],
  telehealth: [],
};

// TPR practitioners are configured and resolved by NPI; their Athena provider IDs are populated dynamically.
// This matrix mirrors the Camelback/Biltmore Athena scheduling-slot guide. Generic Any types are
// provider-specific and are offered only alongside a configured visit type. Empty type lists remain unschedulable.
const TPR_BILTMORE_PROVIDER_OPEN_APPOINTMENT_TYPES: Readonly<
  Record<TprBiltmoreNetworkProviderNpi, TprBiltmoreProviderOpenAppointmentTypes>
> = {
  // Diana Thayer
  [TprBiltmoreCamelbackProviderNpi.DIANA_THAYER]: {
    any: TprBiltmoreOpenAppointmentTypeId.ANY_15,
    newPatient: [],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15],
    hospitalFollowUp: [],
    telehealth: [],
  },
  // Danielle Sturm (Lorde)
  [TprBiltmoreCamelbackProviderNpi.DANIELLE_STURM]: {
    any: TprBiltmoreOpenAppointmentTypeId.ANY_15,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_15],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15],
    hospitalFollowUp: [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_15],
    telehealth: [TprBiltmoreOpenAppointmentTypeId.TELEMED_15],
  },
  // Lorraine Homes
  [TprBiltmoreCamelbackProviderNpi.LORRAINE_HOMES]: {
    any: TprBiltmoreOpenAppointmentTypeId.ANY_15,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_15],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15],
    hospitalFollowUp: [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_15],
    telehealth: [TprBiltmoreOpenAppointmentTypeId.TELEMED_15],
  },
  // Chris Lichtenwalter
  [TprBiltmoreCamelbackProviderNpi.CHRIS_LICHTENWALTER]: {
    any: TprBiltmoreOpenAppointmentTypeId.ANY_10,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_10],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_10],
    hospitalFollowUp: [],
    telehealth: [TprBiltmoreOpenAppointmentTypeId.TELEMED_10],
  },
  // Marium Muzaffar
  [TprBiltmoreCamelbackProviderNpi.MARIUM_MUZAFFAR]: {
    any: TprBiltmoreOpenAppointmentTypeId.ANY_15,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_15],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15],
    hospitalFollowUp: [],
    telehealth: [TprBiltmoreOpenAppointmentTypeId.TELEMED_15],
  },
  // Peter Maki
  [TprBiltmoreCamelbackProviderNpi.PETER_MAKI]: {
    any: null,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_15],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15],
    hospitalFollowUp: [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_15],
    telehealth: [TprBiltmoreOpenAppointmentTypeId.TELEMED_15],
  },
  // Timothy Byrne
  [TprBiltmoreCamelbackProviderNpi.TIMOTHY_BYRNE]: {
    any: null,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_15],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15],
    hospitalFollowUp: [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_15],
    telehealth: [],
  },
  // Nickolaus Gramze
  [TprBiltmoreCamelbackProviderNpi.NICKOLAUS_GRAMZE]: {
    // Any 20 is scoped to 20-minute visits; new patients require NP 40.
    any: null,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_40],
    establishedPatient: [
      TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_20,
      TprBiltmoreOpenAppointmentTypeId.ANY_20,
    ],
    hospitalFollowUp: [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_20, TprBiltmoreOpenAppointmentTypeId.ANY_20],
    telehealth: [TprBiltmoreOpenAppointmentTypeId.TELEMED_15],
  },
  // Akram Ibrahim
  [TprBiltmoreCamelbackProviderNpi.AKRAM_IBRAHIM]: {
    any: TprBiltmoreOpenAppointmentTypeId.ANY_10,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_10],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_10],
    hospitalFollowUp: [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_10],
    telehealth: [],
  },
  // Lawrence Kline
  [TprBiltmoreCamelbackProviderNpi.LAWRENCE_KLINE]: {
    any: TprBiltmoreOpenAppointmentTypeId.ANY_15,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_15],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15],
    hospitalFollowUp: [],
    telehealth: [TprBiltmoreOpenAppointmentTypeId.TELEMED_15],
  },
  // Neal Klein
  [TprBiltmoreCamelbackProviderNpi.NEAL_KLEIN]: {
    any: TprBiltmoreOpenAppointmentTypeId.ANY_15,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_15],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15],
    hospitalFollowUp: [],
    telehealth: [TprBiltmoreOpenAppointmentTypeId.TELEMED_15],
  },
  // Kristen Jensen
  [TprBiltmoreCamelbackProviderNpi.KRISTEN_JENSEN]: {
    any: TprBiltmoreOpenAppointmentTypeId.ANY_15,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_15],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15],
    hospitalFollowUp: [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_15],
    telehealth: [TprBiltmoreOpenAppointmentTypeId.TELEMED_15],
  },
  // Jody Eckhardt
  [TprBiltmoreCamelbackProviderNpi.JODY_ECKHARDT]: {
    any: null,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_30],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_30],
    hospitalFollowUp: [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_30],
    telehealth: [TprBiltmoreOpenAppointmentTypeId.TELEMED_15],
  },
  // Kevin Murphy
  [TprBiltmoreCamelbackProviderNpi.KEVIN_MURPHY]: {
    any: TprBiltmoreOpenAppointmentTypeId.ANY_30,
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_30],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_30],
    hospitalFollowUp: [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_30],
    telehealth: [TprBiltmoreOpenAppointmentTypeId.TELEMED_15],
  },
  // Elizabeth Steffens
  [TprBiltmoreCamelbackProviderNpi.ELIZABETH_STEFFENS]: {
    any: null,
    newPatient: [],
    establishedPatient: [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_30],
    hospitalFollowUp: [],
    telehealth: [],
  },
  [TprBiltmoreCamelbackProviderNpi.RENZO_CATALDO]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreCamelbackProviderNpi.AKIL_LOLI]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreCamelbackProviderNpi.MARWAN_BAHU]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreAhiProviderNpi.MICHELLE_MIX]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreAhiProviderNpi.AYMAN_JAMAL]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreCasaGrandeProviderNpi.JASON_COOL]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreWestProviderNpi.JAMES_KIM]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreWestProviderNpi.RICHARD_PHAM]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreGoodyearProviderNpi.SHARON_SADEGHINIA]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreArrowheadProviderNpi.RAHOOL_KARNIK]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreArrowheadProviderNpi.KRISTINA_SKINNER]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreArrowheadProviderNpi.DAVID_PRUTZMAN]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreArrowheadProviderNpi.MAEDEH_KHOLGHI]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreScottsdaleProviderNpi.ASHISH_GUPTA]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmoreScottsdaleProviderNpi.WILLIAM_JAFFE]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
  [TprBiltmorePeoriaProviderNpi.SAIFULDEEN_AL_QAISI]: TPR_BILTMORE_UNCONFIRMED_OPEN_APPOINTMENT_TYPES,
};

function isTprBiltmoreNetworkProviderNpi(providerNpi: string): providerNpi is TprBiltmoreNetworkProviderNpi {
  return Object.hasOwn(TPR_BILTMORE_PROVIDER_OPEN_APPOINTMENT_TYPES, providerNpi);
}

export function isTprBiltmoreGroup(group: Pick<Group, "subdomain">): boolean {
  return getGroupConfigSubdomain(group) === ConfigSubdomain.TPR;
}

export function getTprBiltmoreAppointmentTypeId(
  group: Pick<Group, "subdomain">,
  appointmentType: AppointmentType
): number | null {
  if (!isTprBiltmoreGroup(group)) return null;

  const appointmentTypeId = TPR_BILTMORE_APPOINTMENT_TYPE_IDS[appointmentType];

  if (appointmentTypeId === undefined) {
    logAndThrow("No TPR Biltmore appointment type configured", { appointmentType });
  }

  return appointmentTypeId;
}

export function getTprBiltmoreOpenAppointmentTypeIds(
  group: Pick<Group, "subdomain">,
  providerNpi: string | null | undefined,
  appointmentType: AppointmentType
): readonly number[] | null {
  if (!isTprBiltmoreGroup(group)) return null;

  if (!providerNpi) {
    logAndThrow("Provider NPI is required to resolve TPR Biltmore open appointment types", { appointmentType });
  }

  if (!isTprBiltmoreNetworkProviderNpi(providerNpi)) {
    logAndThrow("No TPR Biltmore open appointment types configured for provider", { providerNpi, appointmentType });
  }

  const mapping = TPR_BILTMORE_PROVIDER_OPEN_APPOINTMENT_TYPES[providerNpi];

  // The Athena guide records providers with Telemed slots, but TPR's Voice policy currently allows
  // virtual scheduling only with Chris Lichtenwalter. Enforce that policy at availability and booking.
  if (appointmentType === AppointmentType.TELEHEALTH && !isTprBiltmoreTelehealthProviderNpi(providerNpi)) {
    return [];
  }

  let useCaseTypeIds: readonly TprBiltmoreOpenAppointmentTypeId[];

  switch (appointmentType) {
    case AppointmentType.INITIAL_CONSULTATION:
    case AppointmentType.NEW_PATIENT_CHECKIN:
      useCaseTypeIds = mapping.newPatient;
      break;
    case AppointmentType.FOLLOW_UP:
      useCaseTypeIds = mapping.hospitalFollowUp;
      break;
    case AppointmentType.EXISTING_PATIENT_CHECKIN:
      useCaseTypeIds = mapping.establishedPatient;
      break;
    case AppointmentType.TELEHEALTH:
      useCaseTypeIds = mapping.telehealth;
      break;
    default:
      logAndThrow("No TPR Biltmore open appointment types configured for appointment type", {
        providerNpi,
        appointmentType,
      });
  }

  return useCaseTypeIds.length > 0 && mapping.any !== null ? [...useCaseTypeIds, mapping.any] : useCaseTypeIds;
}

export function getTprBiltmoreBookedTypeIdFromOpenTypeId(
  group: Pick<Group, "subdomain">,
  providerNpi: string | null | undefined,
  openAppointmentTypeId: string,
  internalAppointmentType: AppointmentType | null
): number | null {
  if (!isTprBiltmoreGroup(group)) return null;

  if (!providerNpi) {
    logAndThrow("No TPR Biltmore provider NPI configured for open appointment type", {
      openAppointmentTypeId,
      internalAppointmentType,
    });
  }

  if (!internalAppointmentType) {
    logAndThrow("No TPR Biltmore booking type configured for open appointment type", {
      providerNpi,
      openAppointmentTypeId,
    });
  }

  const eligibleOpenAppointmentTypeIds = getTprBiltmoreOpenAppointmentTypeIds(
    group,
    providerNpi,
    internalAppointmentType
  );
  const openAppointmentTypeIdNumber = Number(openAppointmentTypeId);

  if (!eligibleOpenAppointmentTypeIds?.includes(openAppointmentTypeIdNumber)) {
    logAndThrow("Open appointment type is not configured for TPR Biltmore provider and appointment type", {
      providerNpi,
      openAppointmentTypeId,
      internalAppointmentType,
    });
  }

  if (!TPR_BILTMORE_ANY_OPEN_APPOINTMENT_TYPE_IDS.has(openAppointmentTypeIdNumber)) {
    return openAppointmentTypeIdNumber;
  }

  const pairedAppointmentTypeId = eligibleOpenAppointmentTypeIds.find(
    (appointmentTypeId) => !TPR_BILTMORE_ANY_OPEN_APPOINTMENT_TYPE_IDS.has(appointmentTypeId)
  );

  if (pairedAppointmentTypeId === undefined) {
    logAndThrow("No specific TPR Biltmore appointment type is paired with generic open appointment type", {
      providerNpi,
      openAppointmentTypeId,
      internalAppointmentType,
    });
  }

  return pairedAppointmentTypeId;
}

export const tprAthenaConfig = defineAthenaTenantConfig({
  subdomain: ConfigSubdomain.TPR,
  core: {
    practiceId: 600,
    departments: {
      [TPR_BILTMORE_DEPARTMENT_ID]: { name: "Biltmore Cardiology - Camelback", assignee: null },
    },
    patientPrimaryDepartment: PatientPrimaryDepartment.RECEIVING_LOCATION,
    defaultReceivingLocationEhrId: TPR_BILTMORE_DEPARTMENT_ID,
  },
  patient: {
    consent: {
      consenttocall: true,
      consenttotext: true,
      isprivacynoticeonfile: true,
      medicationhistoryconsentverified: null,
    },
    updateInsurance: false,
    referralSource: () => ({
      referralsourceid: TPR_REFERRAL_SOURCE_ID,
      referralsourceother: "",
    }),
    referringProviderMatchStrategy: AthenaReferringProviderMatchStrategy.NPI_FIRST_IGNORE_ZIP,
  },
  insurance: {
    patientCoverageDepartmentId: TPR_BILTMORE_DEPARTMENT_ID,
    resolveCasePolicy: (_memberId, coverage) => resolveTprCasePolicyPackageId(coverage),
  },
  documents: {
    referralDocumentTypeId,
  },
  scheduling: {
    bookedAppointmentType: getTprBiltmoreAppointmentTypeFromBookedEhrId,
    resolveAppointmentTypeId: ({ group, appointmentType }) => {
      const appointmentTypeId = getTprBiltmoreAppointmentTypeId(group, appointmentType);

      if (appointmentTypeId === null) {
        logAndThrow("No TPR Biltmore appointment type configured", { appointmentType });
      }

      return appointmentTypeId;
    },
  },
});
